import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Container,
  Chip,
  Fade,
  useTheme,
  useMediaQuery,
  Avatar,
  LinearProgress,
  Paper,
  Tab,
  Tabs,
  Fab,
} from "@mui/material";
import {
  Schedule,
  AutoAwesome,
  Image as ImageIcon,
  Public,
  Speed,
  TrendingUp,
  Analytics,
  Campaign,
  CheckCircle,
  Rocket,
  Stars,
  Bolt,
  Zap,
  PlayCircle,
  ArrowForward,
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  YouTube,
  Pinterest,
  WhatsApp,
  Telegram,
  VideoLibrary,
  Security,
  CloudSync,
  Psychology,
  Timeline,
  Group,
  BusinessCenter,
  LocalOffer,
  Insights,
  AutoGraph,
  Language,
  CalendarMonth,
  MonetizationOn,
  School,
  Support,
  VerifiedUser,
  ThumbUp,
  Share,
  Comment,
  Favorite,
  Add,
} from "@mui/icons-material";

// Import the new CustomerRequestForm component
import CustomerRequestForm from "./CustomerRequestForm";
import { fetchPricingPlans } from "../../Action/api";
import Footer from "./Footer";
import { useNavigate } from "react-router-dom";
import oruclicksLogo from "../../Assets/images/oruclicks-logo.svg";

export const LandingPage = ({ onGetStartedClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  // Simplified state - only need to track if form is open
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [requestType, setRequestType] = useState("GetStarted");
  const [selectedPlan, setSelectedPlan] = useState(null); // Add this state
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' or 'yearly'
  const [pricingPlans, setPricingPlans] = useState([]);

  // Detect India via browser timezone; fall back to locale
  const isIndia = (() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    return tz.includes("Calcutta") || tz.includes("Kolkata");
  })();
  const USD_RATE = 84; // 1 USD ≈ 84 INR (fixed display rate)

  // Returns the display price string in the correct currency
  const getFormattedPrice = (price) => {
    if (!price || price === "Free" || price === "Custom") return price;
    if (isIndia) return price;
    const num = parseFloat(price.replace(/[^0-9.]/g, ""));
    if (isNaN(num)) return price;
    return `$${Math.round(num / USD_RATE)}`;
  };

  // Returns the discounted (yearly) display price
  const getYearlyPrice = (price) => {
    const formatted = getFormattedPrice(price);
    if (!formatted || formatted === "Free" || formatted === "Custom") return formatted;
    const sym = formatted.replace(/[\d.,]+/, "");
    const num = parseFloat(formatted.replace(/[^0-9.]/g, ""));
    return `${sym}${Math.round(num * 0.8)}`;
  };

  useEffect(() => {
    fetchPricingPlans("pricePlan").then((result) => {
      if (result.success) setPricingPlans(result.data);
    });
  }, []);

  // Animation states
  const [hoveredCard, setHoveredCard] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [liveMetrics, setLiveMetrics] = useState({
    posts: 2847,
    engagement: 94.2,
    reach: 1.2,
    conversions: 15.8,
  });

  // Floating button visibility
  const [showFloatingButton, setShowFloatingButton] = useState(false);

  // Simulate live data updates
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveMetrics((prev) => ({
        posts: prev.posts + Math.floor(Math.random() * 3),
        engagement: prev.engagement + (Math.random() - 0.5) * 0.5,
        reach: prev.reach + (Math.random() - 0.5) * 0.1,
        conversions: prev.conversions + (Math.random() - 0.5) * 0.3,
      }));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Handle scroll for floating button visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const windowHeight = window.innerHeight;

      // Show floating button after scrolling past hero section
      setShowFloatingButton(scrollTop > windowHeight * 0.5);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleGetStarted = (planType = null) => {
    // If triggered from a pricing plan button, go to login directly
    if (planType) {
      navigate("/login", { state: { selectedPlan: planType } });
      return;
    }
    setRequestType("GetStarted");
    setSelectedPlan(planType);
    setContactFormOpen(true);
  };

  const handleStartFreeTrial = (planType = null) => {
    setRequestType("Start Free Trial");
    setSelectedPlan(planType);
    setContactFormOpen(true);
  };

  const handleWatchDemo = (planType = null) => {
    setRequestType("WatchDemo");
    setSelectedPlan(planType);
    setContactFormOpen(true);
  };

  const handleContactFormClose = () => {
    setContactFormOpen(false);
    setRequestType("GetStarted");
    setSelectedPlan(null); // Reset selected plan
  };

  const socialPlatforms = [
    { icon: <Facebook />, name: "Facebook", color: "#1877f2", users: "2.9B" },
    { icon: <Instagram />, name: "Instagram", color: "#e4405f", users: "2B" },
    { icon: <LinkedIn />, name: "LinkedIn", color: "#0077b5", users: "900M" },
    // Phase 2
    // { icon: <Twitter />, name: "Twitter", color: "#1da1f2", users: "450M" },
    // { icon: <YouTube />, name: "YouTube", color: "#ff0000", users: "2.7B" },
    // { icon: <VideoLibrary />, name: "TikTok", color: "#000000", users: "1B" },
    // { icon: <Pinterest />, name: "Pinterest", color: "#bd081c", users: "450M" },
    // { icon: <WhatsApp />, name: "WhatsApp", color: "#25d366", users: "2.8B" },
  ];

  const features = [
    {
      icon: <Psychology sx={{ fontSize: 40 }} />,
      title: "AI Content Generator",
      description: "GPT-powered content creation with brand voice learning",
      color: "#667eea",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      stats: "10x faster",
      details:
        "Creates engaging posts, captions, and hashtags tailored to your brand",
    },
    {
      icon: <Group sx={{ fontSize: 40 }} />,
      title: "One-Click Bulk Account Posting",
      description: "Create and manage 100+ accounts with a single click",
      color: "#f093fb",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      stats: "100+ accounts",
      details: "Mass account setup, bulk posting workflows. Ideal for agencies and enterprise teams managing multiple clients, brands, or channels.",
    },
    {
      icon: <Timeline sx={{ fontSize: 40 }} />,
      title: "Advanced Calendar Scheduling",
      description: "Plan and schedule activities up to 2 months in advance",
      color: "#4facfe",
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      stats: "2 months ahead",
      details: "Smart scheduling engine with automated execution. Prepare campaigns early and ensure consistent publishing without daily intervention.",
    },
    {
      icon: <CloudSync sx={{ fontSize: 40 }} />,
      title: "Marketing Asset Management",
      description: "Unlimited storage for all your marketing assets",
      color: "#43e97b",
      gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      stats: "Unlimited storage",
      details: "Store images, videos, documents, and creatives in one centralized library. Supports multiple formats with team-wide access control.",
    },
    {
      icon: <CalendarMonth sx={{ fontSize: 40 }} />,
      title: "Visual Calendar View",
      description: "Complete visual overview of all scheduled campaigns",
      color: "#a78bfa",
      gradient: "linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)",
      stats: "360° visibility",
      details: "Interactive calendar with drag-and-drop scheduling. Track timelines, avoid conflicts, and maintain clear visibility across teams.",
    },
    {
      icon: <CheckCircle sx={{ fontSize: 40 }} />,
      title: "Structured Approval Process",
      description: "Multi-level approval workflow before content goes live",
      color: "#fb923c",
      gradient: "linear-gradient(135deg, #fb923c 0%, #f97316 100%)",
      stats: "100% compliance",
      details: "Role-based approval controls with audit trail tracking. Ensure quality, compliance, and brand consistency at every stage.",
    },
    {
      icon: <BusinessCenter sx={{ fontSize: 40 }} />,
      title: "End-to-End User Management",
      description: "Manage users, roles, permissions, and access levels",
      color: "#14b8a6",
      gradient: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
      stats: "Role-based access",
      details: "Department-level permissions with activity tracking. Designed for organizations with multiple teams and responsibility layers.",
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: "Enterprise Security",
      description: "Bank-level security with SOC 2 compliance",
      color: "#ef4444",
      gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
      stats: "99.9% uptime",
      details: "Enterprise-grade infrastructure with global CDN and 24/7 monitoring.",
    },
  ];

  const advancedFeatures = [
    {
      icon: <Timeline />,
      title: "Visual Content Calendar",
      description: "Drag-and-drop scheduling with team collaboration",
    },
    {
      icon: <Group />,
      title: "Team Management",
      description: "Role-based access control and approval workflows",
    },
    {
      icon: <BusinessCenter />,
      title: "Client Management",
      description: "White-label solutions for agencies and consultants",
    },
    {
      icon: <LocalOffer />,
      title: "Campaign Optimization",
      description: "A/B testing and performance optimization tools",
    },
    {
      icon: <Insights />,
      title: "Competitor Analysis",
      description: "Track competitors and industry benchmarks",
    },
    {
      icon: <Language />,
      title: "Multi-language Support",
      description: "Create content in 50+ languages with AI translation",
    },
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Marketing Director",
      company: "TechFlow Inc",
      avatar: "/api/placeholder/64/64",
      rating: 5,
  text: "OruClick increased our social media ROI by 400%. The AI content generation is incredible!",
      metrics: { followers: "+245%", engagement: "+180%", leads: "+320%" },
    },
    {
      name: "Marcus Rodriguez",
      role: "Founder",
      company: "GrowthLab",
      avatar: "/api/placeholder/64/64",
      rating: 5,
      text: "Cut our content creation time from 20 hours to 2 hours per week. Game changer!",
      metrics: { time: "-90%", posts: "+500%", reach: "+280%" },
    },
    {
      name: "Emily Watson",
      role: "Social Media Manager",
      company: "BrandForge",
      avatar: "/api/placeholder/64/64",
      rating: 5,
      text: "The predictive analytics helped us achieve 95% engagement rate improvements.",
      metrics: { engagement: "+95%", clicks: "+150%", conversions: "+220%" },
    },
  ];

  const stats = [
    { value: "50K+", label: "Active Users", icon: "👥" },
    { value: "99.9%", label: "Uptime", icon: "⚡" },
    { value: "2.5M", label: "Posts Created", icon: "📝" },
    { value: "150+", label: "Countries", icon: "🌍" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", overflow: "hidden", position: "relative" }}>
      {/* Floating Get Started Button */}
      <Fade in={showFloatingButton}>
        <Fab
          color="primary"
          onClick={() => handleGetStarted()}
          sx={{
            position: "fixed",
            bottom: { xs: 20, md: 30 },
            right: { xs: 20, md: 30 },
            zIndex: 1000,
            background: "linear-gradient(135deg, #667eea, #764ba2)",
            color: "white",
            width: { xs: 60, md: 80 },
            height: { xs: 60, md: 80 },
            fontSize: { xs: "0.9rem", md: "1.1rem" },
            fontWeight: "bold",
            boxShadow: "0 8px 25px rgba(102,126,234,0.4)",
            "&:hover": {
              background: "linear-gradient(135deg, #5a67d8, #6b46c1)",
              transform: "scale(1.05)",
              boxShadow: "0 12px 35px rgba(102,126,234,0.6)",
            },
            animation: "pulse 3s infinite",
            "@keyframes pulse": {
              "0%": {
                boxShadow: "0 8px 25px rgba(102,126,234,0.4)",
                transform: "scale(1)",
              },
              "50%": {
                boxShadow: "0 12px 35px rgba(102,126,234,0.7)",
                transform: "scale(1.02)",
              },
              "100%": {
                boxShadow: "0 8px 25px rgba(102,126,234,0.4)",
                transform: "scale(1)",
              },
            },
          }}
        >
          {isMobile ? <Rocket /> : "Get Started"}
        </Fab>
      </Fade>

      {/* Hero Section - Enhanced */}
      <Box
        sx={{
          minHeight: "100vh",
          position: "relative",
          background:
            "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
          color: "white",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Enhanced Animated Background */}
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            overflow: "hidden",
            zIndex: 1,
          }}
        >
          {/* Floating particles */}
          {[...Array(20)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                width: `${20 + Math.random() * 80}px`,
                height: `${20 + Math.random() * 80}px`,
                background: `rgba(255,255,255,${0.03 + Math.random() * 0.07})`,
                borderRadius: Math.random() > 0.5 ? "50%" : "20%",
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animation: `float ${
                  10 + Math.random() * 20
                }s infinite ease-in-out ${Math.random() * 5}s`,
                filter: "blur(1px)",
                "@keyframes float": {
                  "0%, 100%": { transform: "translate(0, 0) rotate(0deg)" },
                  "25%": {
                    transform: `translate(${Math.random() * 50}px, ${
                      Math.random() * 50
                    }px) rotate(90deg)`,
                  },
                  "50%": {
                    transform: `translate(-${Math.random() * 50}px, ${
                      Math.random() * 50
                    }px) rotate(180deg)`,
                  },
                  "75%": {
                    transform: `translate(${Math.random() * 50}px, -${
                      Math.random() * 50
                    }px) rotate(270deg)`,
                  },
                },
              }}
            />
          ))}

          {/* Mesh gradient overlay */}
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              background: `
                radial-gradient(circle at 20% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(255,255,255,0.05) 0%, transparent 50%),
                radial-gradient(circle at 40% 60%, rgba(255,255,255,0.03) 0%, transparent 50%)
              `,
              animation: "meshMove 15s ease-in-out infinite",
              "@keyframes meshMove": {
                "0%, 100%": { opacity: 0.5 },
                "50%": { opacity: 0.8 },
              },
            }}
          />
        </Box>

        <Container
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            position: "relative",
            zIndex: 2,
          }}
        >
          {/* Enhanced Header */}
          <Box sx={{ pt: 4, pb: 2, textAlign: "center" }}>
            {/* Animated Logo */}
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: "30%",
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(30px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 4,
                border: "3px solid rgba(255,255,255,0.3)",
                position: "relative",
                animation: "logoFloat 6s ease-in-out infinite",
                "&::before, &::after": {
                  content: '""',
                  position: "absolute",
                  borderRadius: "50%",
                  border: "2px solid rgba(255,255,255,0.15)",
                },
                "&::before": {
                  width: "150px",
                  height: "150px",
                  animation: "pulse1 4s infinite",
                },
                "&::after": {
                  width: "180px",
                  height: "180px",
                  animation: "pulse2 4s infinite 2s",
                },
                "@keyframes logoFloat": {
                  "0%, 100%": { transform: "translateY(0px)" },
                  "50%": { transform: "translateY(-12px)" },
                },
                "@keyframes pulse1": {
                  "0%": { transform: "scale(1)", opacity: 0.7 },
                  "100%": { transform: "scale(1.25)", opacity: 0 },
                },
                "@keyframes pulse2": {
                  "0%": { transform: "scale(1)", opacity: 0.5 },
                  "100%": { transform: "scale(1.35)", opacity: 0 },
                },
              }}
            >
              <Box
                component="img"
                src={oruclicksLogo}
                alt="OruClick Logo"
                sx={{
                  width: 90,
                  height: 90,
                  objectFit: "contain",
                  borderRadius: "20%",
                  animation: "logoScalePulse 3s ease-in-out infinite",
                  "@keyframes logoScalePulse": {
                    "0%, 100%": { transform: "scale(1)", filter: "drop-shadow(0 0 6px rgba(99,102,241,0.7))" },
                    "50%": { transform: "scale(1.08)", filter: "drop-shadow(0 0 20px rgba(34,211,238,0.9))" },
                  },
                }}
              />
            </Box>

            <Fade in timeout={1000}>
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 900,
                  mb: 3,
                  fontSize: { xs: "2.5rem", md: "4.5rem" },
                  textShadow: "0 8px 32px rgba(0,0,0,0.3)",
                  background: "linear-gradient(45deg, #fff, #f8fafc, #e2e8f0)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "-0.02em",
                }}
              >
                OruClick
              
              </Typography>
            </Fade>

            <Fade in timeout={1500}>
              <Typography
                variant="h4"
                sx={{
                  mb: 2,
                  opacity: 0.95,
                  maxWidth: "800px",
                  mx: "auto",
                  fontWeight: 300,
                  letterSpacing: "0.5px",
                  fontSize: { xs: "1.15rem", sm: "1.5rem", md: "2.125rem" },
                }}
              >
                Complete Social Media Management Platform with Enterprise-Grade Features
              </Typography>
            </Fade>

            <Fade in timeout={2000}>
              <Typography
                variant="h6"
                sx={{
                  mb: 4,
                  opacity: 0.8,
                  maxWidth: "600px",
                  mx: "auto",
                  fontWeight: 400,
                  fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.25rem" },
                }}
              >
                Generate viral content, schedule posts across all platforms, and
                grow your audience with cutting-edge AI technology
              </Typography>
            </Fade>

            {/* Live Stats Banner - Enhanced */}
            <Fade in timeout={3000}>
              <Box
                sx={{
                  background: "rgba(255,255,255,0.1)",
                  backdropFilter: "blur(30px)",
                  border: "2px solid rgba(255,255,255,0.3)",
                  borderRadius: 6,
                  p: 4,
                  mb: 4,
                  display: "flex",
                  justifyContent: "space-around",
                  flexWrap: "wrap",
                  gap: { xs: 2, md: 3 },
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(240,147,251,0.1) 100%)",
                    animation: "shimmer 3s infinite",
                  },
                  "@keyframes shimmer": {
                    "0%, 100%": { opacity: 0.3 },
                    "50%": { opacity: 0.6 },
                  },
                }}
              >
                {stats.map((stat, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      textAlign: "center", 
                      width: { xs: "calc(50% - 16px)", sm: "auto" },
                      minWidth: { xs: "auto", sm: 140 },
                      position: "relative",
                      zIndex: 1,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "scale(1.1) translateY(-5px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.05))",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                        fontSize: "2rem",
                        position: "relative",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
                        animation: `float ${3 + index * 0.5}s ease-in-out infinite`,
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          width: "100%",
                          height: "100%",
                          borderRadius: "50%",
                          border: "2px solid rgba(255,255,255,0.3)",
                          animation: `ripple ${2 + index * 0.3}s infinite`,
                        },
                        "@keyframes float": {
                          "0%, 100%": { transform: "translateY(0px)" },
                          "50%": { transform: "translateY(-10px)" },
                        },
                        "@keyframes ripple": {
                          "0%": { transform: "scale(1)", opacity: 1 },
                          "100%": { transform: "scale(1.4)", opacity: 0 },
                        },
                      }}
                    >
                      {stat.icon}
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{ 
                        fontWeight: "900", 
                        mb: 0.5,
                        background: "linear-gradient(135deg, #fff 0%, #e0e7ff 100%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        textShadow: "0 0 30px rgba(255,255,255,0.3)",
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        opacity: 0.9,
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Fade>
          </Box>

          {/* Enhanced Main Dashboard Preview */}
          <Fade in timeout={3500}>
            <Box sx={{ flex: 1, mb: 4 }}>
              <Grid container spacing={4}>
                {/* Left Panel - Live Analytics */}
                <Grid item xs={12} md={4}>
                  <Paper
                    sx={{
                      background: "rgba(255,255,255,0.1)",
                      backdropFilter: "blur(20px)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      borderRadius: 4,
                      p: 3,
                      height: "450px",
                      color: "white",
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
                      📊 Live Performance Dashboard
                    </Typography>

                    {/* Real-time metrics */}
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2">Posts Published</Typography>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                          {liveMetrics.posts.toLocaleString()}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={75}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: "rgba(255,255,255,0.2)",
                          "& .MuiLinearProgress-bar": {
                            background:
                              "linear-gradient(90deg, #4facfe, #00f2fe)",
                          },
                        }}
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2">Engagement Rate</Typography>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                          {liveMetrics.engagement.toFixed(1)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={liveMetrics.engagement}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: "rgba(255,255,255,0.2)",
                          "& .MuiLinearProgress-bar": {
                            background:
                              "linear-gradient(90deg, #f093fb, #f5576c)",
                          },
                        }}
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2">
                          Reach (Millions)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                          {liveMetrics.reach.toFixed(1)}M
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={60}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: "rgba(255,255,255,0.2)",
                          "& .MuiLinearProgress-bar": {
                            background:
                              "linear-gradient(90deg, #43e97b, #38f9d7)",
                          },
                        }}
                      />
                    </Box>

                    {/* Mini chart */}
                    <Box
                      sx={{
                        background: "rgba(255,255,255,0.05)",
                        borderRadius: 2,
                        height: 120,
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "space-around",
                        p: 2,
                        mt: 2,
                      }}
                    >
                      {[...Array(12)].map((_, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 16,
                            height: `${
                              20 + Math.sin(i * 0.5) * 40 + Math.random() * 20
                            }%`,
                            background: `linear-gradient(to top, 
                              rgba(255,255,255,0.3), 
                              ${
                                i % 3 === 0
                                  ? "rgba(67,233,123,0.8)"
                                  : i % 3 === 1
                                  ? "rgba(79,172,254,0.8)"
                                  : "rgba(240,147,251,0.8)"
                              }
                            )`,
                            borderRadius: "2px 2px 0 0",
                            animation: `barGrow 2s ease-out ${i * 0.1}s`,
                            "@keyframes barGrow": {
                              "0%": { height: "0%" },
                              "100%": {
                                height: `${
                                  20 +
                                  Math.sin(i * 0.5) * 40 +
                                  Math.random() * 20
                                }%`,
                              },
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                </Grid>

                {/* Center Panel - Interactive Features */}
                <Grid item xs={12} md={4}>
                  <Paper
                    sx={{
                      background: "rgba(255,255,255,0.1)",
                      backdropFilter: "blur(20px)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      borderRadius: 4,
                      p: 3,
                      height: "450px",
                      color: "white",
                    }}
                  >
                    <Box
                      sx={{
                        borderBottom: 1,
                        borderColor: "rgba(255,255,255,0.2)",
                        mb: 2,
                      }}
                    >
                      <Tabs
                        value={activeTab}
                        onChange={(e, newValue) => setActiveTab(newValue)}
                        textColor="inherit"
                        indicatorColor="primary"
                      >
                        <Tab label="AI Content" sx={{ color: "white" }} />
                        <Tab label="Scheduler" sx={{ color: "white" }} />
                        <Tab label="Analytics" sx={{ color: "white" }} />
                      </Tabs>
                    </Box>

                    {activeTab === 0 && (
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, fontWeight: "bold" }}
                        >
                          🤖 AI Content Generator
                        </Typography>
                        <Box
                          sx={{
                            background: "rgba(255,255,255,0.1)",
                            borderRadius: 2,
                            p: 2,
                            mb: 2,
                            border: "1px dashed rgba(255,255,255,0.3)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontStyle: "italic", mb: 1 }}
                          >
                            "Generate a viral LinkedIn post about AI
                            innovation..."
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <AutoAwesome sx={{ fontSize: 16 }} />
                            <Typography variant="caption">
                              Generating...
                            </Typography>
                            <LinearProgress
                              sx={{ flex: 1, height: 4, borderRadius: 2 }}
                            />
                          </Box>
                        </Box>

                        {/* Generated content preview */}
                        <Paper
                          sx={{
                            background: "rgba(255,255,255,0.05)",
                            p: 2,
                            borderRadius: 2,
                          }}
                        >
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            "🚀 The future of AI is here! Just witnessed
                            groundbreaking innovation that will change
                            everything. The possibilities are endless... #AI
                            #Innovation #TechLeadership"
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                            <Chip
                              label="#AI"
                              size="small"
                              sx={{ bgcolor: "rgba(255,255,255,0.2)" }}
                            />
                            <Chip
                              label="#Innovation"
                              size="small"
                              sx={{ bgcolor: "rgba(255,255,255,0.2)" }}
                            />
                            <Chip
                              label="#Tech"
                              size="small"
                              sx={{ bgcolor: "rgba(255,255,255,0.2)" }}
                            />
                          </Box>
                        </Paper>
                      </Box>
                    )}

                    {activeTab === 1 && (
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, fontWeight: "bold" }}
                        >
                          📅 Smart Scheduler
                        </Typography>
                        {/* Calendar view mockup */}
                        <Grid container spacing={1}>
                          {[...Array(21)].map((_, i) => (
                            <Grid item xs={12 / 7} key={i}>
                              <Box
                                sx={{
                                  height: 40,
                                  background:
                                    i % 7 === 0 || i % 7 === 6
                                      ? "rgba(255,255,255,0.05)"
                                      : Math.random() > 0.7
                                      ? "rgba(67,233,123,0.3)"
                                      : "rgba(255,255,255,0.1)",
                                  borderRadius: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.8rem",
                                  border:
                                    Math.random() > 0.8
                                      ? "1px solid rgba(240,147,251,0.5)"
                                      : "none",
                                }}
                              >
                                {i + 1}
                              </Box>
                            </Grid>
                          ))}
                        </Grid>

                        <Typography
                          variant="body2"
                          sx={{ mt: 2, opacity: 0.8 }}
                        >
                          🎯 Optimal posting times detected
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          📊 15 posts scheduled for this week
                        </Typography>
                      </Box>
                    )}

                    {activeTab === 2 && (
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, fontWeight: "bold" }}
                        >
                          📈 Performance Analytics
                        </Typography>

                        {/* Metrics cards */}
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <Paper
                              sx={{
                                p: 1.5,
                                textAlign: "center",
                                bgcolor: "rgba(255,255,255,0.1)",
                              }}
                            >
                              <Typography variant="h6">+45%</Typography>
                              <Typography variant="caption">
                                Engagement
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6}>
                            <Paper
                              sx={{
                                p: 1.5,
                                textAlign: "center",
                                bgcolor: "rgba(255,255,255,0.1)",
                              }}
                            >
                              <Typography variant="h6">+280%</Typography>
                              <Typography variant="caption">Reach</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6}>
                            <Paper
                              sx={{
                                p: 1.5,
                                textAlign: "center",
                                bgcolor: "rgba(255,255,255,0.1)",
                              }}
                            >
                              <Typography variant="h6">+150%</Typography>
                              <Typography variant="caption">
                                Followers
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6}>
                            <Paper
                              sx={{
                                p: 1.5,
                                textAlign: "center",
                                bgcolor: "rgba(255,255,255,0.1)",
                              }}
                            >
                              <Typography variant="h6">+320%</Typography>
                              <Typography variant="caption">
                                Conversions
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>

                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          📊 Real-time insights across all platforms
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* Right Panel - Connected Platforms */}
                <Grid item xs={12} md={4}>
                  <Paper
                    sx={{
                      background: "rgba(255,255,255,0.1)",
                      backdropFilter: "blur(20px)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      borderRadius: 4,
                      p: 3,
                      height: "450px",
                      color: "white",
                      overflow: "hidden", // Prevent overflow
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                      🌐 Connected Platforms
                    </Typography>

                    {/* Scrollable container for platforms */}
                    <Box
                      sx={{
                        height: "380px", // Fixed height for scrollable area
                        overflowY: "auto",
                        pr: 1,
                        "&::-webkit-scrollbar": {
                          width: "4px",
                        },
                        "&::-webkit-scrollbar-track": {
                          background: "rgba(255,255,255,0.1)",
                          borderRadius: "2px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: "rgba(255,255,255,0.3)",
                          borderRadius: "2px",
                          "&:hover": {
                            background: "rgba(255,255,255,0.5)",
                          },
                        },
                      }}
                    >
                      <Grid container spacing={1.5}>
                        {socialPlatforms.map((platform, index) => (
                          <Grid item xs={12} key={index}>
                            <Box
                              sx={{
                                background: "rgba(255,255,255,0.1)",
                                borderRadius: 2,
                                p: 1.5,
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                                minHeight: "60px", // Consistent height
                                "&:hover": {
                                  transform: "translateX(5px)",
                                  background: "rgba(255,255,255,0.15)",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  width: 35,
                                  height: 35,
                                  borderRadius: "50%",
                                  background: platform.color,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "white",
                                  fontSize: "1.2rem",
                                }}
                              >
                                {platform.icon}
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                {" "}
                                {/* minWidth: 0 prevents text overflow */}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: "600",
                                    fontSize: "0.9rem",
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {platform.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    opacity: 0.7,
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  {platform.users} users
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  background: "#4ade80",
                                  flexShrink: 0, // Prevent shrinking
                                  animation: "pulse 2s infinite",
                                  "@keyframes pulse": {
                                    "0%, 100%": { opacity: 1 },
                                    "50%": { opacity: 0.3 },
                                  },
                                }}
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Visual Feature Showcase - NEW */}
      <Box
        sx={{
          py: 8,
          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container>
          <Grid container spacing={6} alignItems="center">
            {/* Left side - Visual Graphics */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: 500,
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {/* Central hub circle */}
                <Box
                  sx={{
                    position: "absolute",
                    width: 200,
                    height: 200,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 20px 60px rgba(102,126,234,0.4)",
                    animation: "pulse 3s infinite",
                    zIndex: 2,
                    "@keyframes pulse": {
                      "0%, 100%": { transform: "scale(1)" },
                      "50%": { transform: "scale(1.05)" },
                    },
                  }}
                >
                  <Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
                    100+
                  </Typography>
                </Box>

                {/* Orbiting feature icons */}
                {[
                  { icon: <Group />, angle: 0, color: "#f093fb", delay: 0 },
                  { icon: <Timeline />, angle: 60, color: "#4facfe", delay: 0.5 },
                  { icon: <CloudSync />, angle: 120, color: "#43e97b", delay: 1 },
                  { icon: <CheckCircle />, angle: 180, color: "#fb923c", delay: 1.5 },
                  { icon: <BusinessCenter />, angle: 240, color: "#a78bfa", delay: 2 },
                  { icon: <Security />, angle: 300, color: "#ef4444", delay: 2.5 },
                ].map((item, index) => {
                  const radius = 180;
                  const x = Math.cos((item.angle * Math.PI) / 180) * radius;
                  const y = Math.sin((item.angle * Math.PI) / 180) * radius;
                  return (
                    <Box
                      key={index}
                      sx={{
                        position: "absolute",
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        background: "white",
                        boxShadow: `0 10px 30px ${item.color}40`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transform: `translate(${x}px, ${y}px)`,
                        animation: `orbit 20s linear infinite ${item.delay}s, floatIcon 3s ease-in-out infinite ${item.delay}s`,
                        zIndex: 1,
                        border: `3px solid ${item.color}`,
                        color: item.color,
                        fontSize: "2rem",
                        "@keyframes orbit": {
                          "0%": { transform: `translate(${x}px, ${y}px) rotate(0deg)` },
                          "100%": { transform: `translate(${x}px, ${y}px) rotate(360deg)` },
                        },
                        "@keyframes floatIcon": {
                          "0%, 100%": { transform: `translate(${x}px, ${y}px) translateY(0px)` },
                          "50%": { transform: `translate(${x}px, ${y}px) translateY(-10px)` },
                        },
                      }}
                    >
                      {item.icon}
                    </Box>
                  );
                })}

                {/* Connecting lines */}
                <Box
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    "&::before, &::after": {
                      content: '""',
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      width: 360,
                      height: 360,
                      borderRadius: "50%",
                      border: "2px dashed rgba(102,126,234,0.2)",
                      transform: "translate(-50%, -50%)",
                    },
                    "&::after": {
                      width: 400,
                      height: 400,
                      animation: "rotate 30s linear infinite",
                    },
                    "@keyframes rotate": {
                      "0%": { transform: "translate(-50%, -50%) rotate(0deg)" },
                      "100%": { transform: "translate(-50%, -50%) rotate(360deg)" },
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* Right side - Content */}
            <Grid item xs={12} md={6}>
              <Box sx={{ pl: { md: 4 } }}>
                <Chip
                  label="✨ All-in-One Platform"
                  sx={{
                    mb: 3,
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    color: "white",
                    fontWeight: "bold",
                    px: 3,
                    py: 2.5,
                    fontSize: "1rem",
                  }}
                />
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: "900",
                    mb: 3,
                    fontSize: { xs: "1.6rem", sm: "2.2rem", md: "3.75rem" },
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Everything You Need in One Place
                </Typography>
                <Typography variant="h6" sx={{ mb: 4, color: "#64748b", lineHeight: 1.8 }}>
                  From bulk account management to advanced scheduling, visual calendars to approval workflows - we've built the complete social media management ecosystem.
                </Typography>

                {/* Feature highlights */}
                <Grid container spacing={2}>
                  {[
                    { label: "100+ Accounts", icon: "👥", color: "#f093fb" },
                    { label: "Unlimited Assets", icon: "🗂", color: "#43e97b" },
                    { label: "2 Month Scheduling", icon: "📅", color: "#4facfe" },
                    { label: "Multi-Level Approvals", icon: "✅", color: "#fb923c" },
                  ].map((item, index) => (
                    <Grid item xs={6} key={index}>
                      <Paper
                        sx={{
                          p: 2.5,
                          textAlign: "center",
                          borderRadius: 3,
                          background: "white",
                          border: `2px solid ${item.color}30`,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-8px)",
                            boxShadow: `0 15px 40px ${item.color}40`,
                            borderColor: item.color,
                          },
                        }}
                      >
                        <Typography sx={{ fontSize: "2rem", mb: 1 }}>{item.icon}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: item.color }}>
                          {item.label}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box
        sx={{
          py: 10,
          background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            width: "500px",
            height: "500px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(102,126,234,0.1) 0%, transparent 70%)",
            top: "-200px",
            right: "-200px",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(240,147,251,0.1) 0%, transparent 70%)",
            bottom: "-150px",
            left: "-150px",
          },
        }}
      >
        <Container sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="h3"
              sx={{ 
                mb: 2, 
                fontWeight: "bold",
                fontSize: { xs: "1.6rem", sm: "2rem", md: "3rem" },
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              🚀 Complete Feature Suite
            </Typography>
            <Typography
              variant="h6"
              sx={{ opacity: 0.7, maxWidth: "600px", mx: "auto" }}
            >
              Everything you need to manage, scale, and automate your social media presence
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    height: "100%",
                    background: "white",
                    borderRadius: 4,
                    boxShadow: hoveredCard === index 
                      ? "0 25px 60px rgba(0,0,0,0.2)" 
                      : "0 10px 30px rgba(0,0,0,0.08)",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden",
                    border: hoveredCard === index 
                      ? "2px solid" 
                      : "2px solid transparent",
                    borderColor: hoveredCard === index ? feature.color : "transparent",
                    transform: hoveredCard === index 
                      ? "translateY(-15px) scale(1.02)" 
                      : "translateY(0) scale(1)",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: feature.gradient,
                      opacity: hoveredCard === index ? 0.05 : 0,
                      transition: "opacity 0.4s ease",
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, position: "relative", zIndex: 1 }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 3,
                        background: feature.gradient,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 3,
                        color: "white",
                        position: "relative",
                        transform: hoveredCard === index ? "scale(1.1) rotate(5deg)" : "scale(1) rotate(0deg)",
                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          width: "100%",
                          height: "100%",
                          borderRadius: 3,
                          background: feature.gradient,
                          opacity: 0.3,
                          filter: "blur(15px)",
                          transform: hoveredCard === index ? "scale(1.2)" : "scale(0.8)",
                          transition: "all 0.4s ease",
                          zIndex: -1,
                        },
                      }}
                    >
                      {feature.icon}
                    </Box>
                    
                    <Chip
                      label={feature.stats}
                      size="small"
                      sx={{
                        mb: 2,
                        background: feature.gradient,
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                        height: "24px",
                      }}
                    />
                    
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: "bold", 
                        mb: 2,
                        fontSize: "1.15rem",
                        lineHeight: 1.3,
                        minHeight: "50px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {feature.title}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mb: 2, 
                        opacity: 0.7,
                        fontWeight: 500,
                        color: feature.color,
                        lineHeight: 1.5,
                      }}
                    >
                      {feature.description}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        opacity: 0.6,
                        lineHeight: 1.6,
                        fontSize: "0.9rem",
                      }}
                    >
                      {feature.details}
                    </Typography>
                  </CardContent>
                  
                  {/* Decorative corner accent */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      width: 60,
                      height: 60,
                      background: feature.gradient,
                      opacity: hoveredCard === index ? 0.15 : 0.08,
                      transition: "all 0.4s ease",
                      clipPath: "polygon(100% 0, 100% 100%, 0 100%)",
                    }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Before/After Comparison - NEW */}
      <Box
        sx={{
          py: 10,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container>
          <Typography
            variant="h3"
            sx={{ textAlign: "center", mb: 2, fontWeight: "bold", fontSize: { xs: "1.6rem", sm: "2rem", md: "3rem" } }}
          >
            ⚡ The OruClick Advantage
          </Typography>
          <Typography
            variant="h6"
            sx={{ textAlign: "center", mb: 8, opacity: 0.9 }}
          >
            See how we transform your social media workflow
          </Typography>

          <Grid container spacing={4}>
            {/* Without OruClick */}
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 4,
                  background: "rgba(255,255,255,0.1)",
                  backdropFilter: "blur(20px)",
                  border: "2px solid rgba(255,255,255,0.2)",
                  height: "100%",
                }}
              >
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                    ❌ Without OruClick
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Traditional manual workflow
                  </Typography>
                </Box>

                {[
                  { icon: "⏰", text: "20+ hours per week on manual posting", color: "#ef4444" },
                  { icon: "😰", text: "Managing accounts one by one", color: "#ef4444" },
                  { icon: "📁", text: "Assets scattered across platforms", color: "#ef4444" },
                  { icon: "🔄", text: "No scheduling or automation", color: "#ef4444" },
                  { icon: "❓", text: "No approval workflows", color: "#ef4444" },
                  { icon: "📊", text: "Limited analytics and insights", color: "#ef4444" },
                ].map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      background: "rgba(239,68,68,0.1)",
                      border: "1px solid rgba(239,68,68,0.3)",
                      animation: `slideIn 0.5s ease-out ${index * 0.1}s backwards`,
                      "@keyframes slideIn": {
                        "0%": { opacity: 0, transform: "translateX(-20px)" },
                        "100%": { opacity: 1, transform: "translateX(0)" },
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: "1.5rem" }}>{item.icon}</Typography>
                    <Typography variant="body1">{item.text}</Typography>
                  </Box>
                ))}
              </Paper>
            </Grid>

            {/* With OruClick */}
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 4,
                  background: "white",
                  color: "#1e293b",
                  height: "100%",
                  boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
                  position: "relative",
                  "&::before": {
                    content: '"✨"',
                    position: "absolute",
                    top: -20,
                    right: -20,
                    fontSize: "4rem",
                    animation: "sparkle 2s infinite",
                  },
                  "@keyframes sparkle": {
                    "0%, 100%": { transform: "scale(1) rotate(0deg)", opacity: 1 },
                    "50%": { transform: "scale(1.2) rotate(180deg)", opacity: 0.8 },
                  },
                }}
              >
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1, color: "#667eea" }}>
                    ✅ With OruClick
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Automated, intelligent, efficient
                  </Typography>
                </Box>

                {[
                  { icon: "⚡", text: "2 hours per week - 90% time saved", color: "#43e97b" },
                  { icon: "🚀", text: "Manage 100+ accounts with one click", color: "#43e97b" },
                  { icon: "☁️", text: "Unlimited centralized asset storage", color: "#43e97b" },
                  { icon: "📅", text: "Schedule 2 months in advance", color: "#43e97b" },
                  { icon: "✅", text: "Multi-level approval workflows", color: "#43e97b" },
                  { icon: "📈", text: "Real-time analytics & insights", color: "#43e97b" },
                ].map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      background: "rgba(67,233,123,0.1)",
                      border: "2px solid rgba(67,233,123,0.3)",
                      animation: `slideInRight 0.5s ease-out ${index * 0.1}s backwards`,
                      "&:hover": {
                        background: "rgba(67,233,123,0.2)",
                        transform: "translateX(5px)",
                        borderColor: "rgba(67,233,123,0.6)",
                      },
                      transition: "all 0.3s ease",
                      "@keyframes slideInRight": {
                        "0%": { opacity: 0, transform: "translateX(20px)" },
                        "100%": { opacity: 1, transform: "translateX(0)" },
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: "1.5rem" }}>{item.icon}</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{item.text}</Typography>
                  </Box>
                ))}
              </Paper>
            </Grid>
          </Grid>

          {/* CTA Button */}
          <Box sx={{ textAlign: "center", mt: 6, px: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => handleGetStarted()}
              sx={{
                background: "white",
                color: "#667eea",
                fontWeight: "bold",
                px: { xs: 3, md: 8 },
                py: { xs: 1.5, md: 3 },
                borderRadius: 10,
                fontSize: { xs: "0.95rem", md: "1.3rem" },
                boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
                width: { xs: "100%", sm: "auto" },
                "&:hover": {
                  background: "#f8fafc",
                  transform: "translateY(-5px) scale(1.05)",
                  boxShadow: "0 25px 60px rgba(0,0,0,0.4)",
                },
              }}
            >
              🚀 Start Your Transformation Now
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Additional Features Grid */}
      <Box sx={{ py: 8, bgcolor: "white" }}>
        <Container>
          <Typography
            variant="h3"
            sx={{ textAlign: "center", mb: 6, fontWeight: "bold", fontSize: { xs: "1.6rem", sm: "2rem", md: "3rem" } }}
          >
            💼 Professional Tools
          </Typography>

          <Grid container spacing={3}>
            {advancedFeatures.map((feature, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Box
                  sx={{
                    p: 3,
                    border: "1px solid #e2e8f0",
                    borderRadius: 3,
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "#667eea",
                      boxShadow: "0 5px 20px rgba(102,126,234,0.1)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 2,
                        background: "linear-gradient(135deg, #667eea, #764ba2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        mr: 2,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {feature.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    {feature.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials */}
      <Box
        sx={{
          py: 8,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }}
      >
        <Container>
          <Typography
            variant="h3"
            sx={{ textAlign: "center", mb: 6, fontWeight: "bold", fontSize: { xs: "1.6rem", sm: "2rem", md: "3rem" } }}
          >
            ⭐ Customer Success Stories
          </Typography>

          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    background: "rgba(255,255,255,0.1)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: 4,
                    p: 3,
                    height: "100%",
                    color: "white",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Avatar
                      src={testimonial.avatar}
                      sx={{ width: 60, height: 60, mr: 2 }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {testimonial.name}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {testimonial.role} at {testimonial.company}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Stars
                            key={i}
                            sx={{ fontSize: 16, color: "#fbbf24" }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{ mb: 3, fontStyle: "italic" }}
                  >
                    "{testimonial.text}"
                  </Typography>

                  <Grid container spacing={1}>
                    {Object.entries(testimonial.metrics).map(
                      ([key, value], i) => (
                        <Grid item xs={4} key={i}>
                          <Box sx={{ textAlign: "center" }}>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold", color: "#4ade80" }}
                            >
                              {value}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                              {key}
                            </Typography>
                          </Box>
                        </Grid>
                      )
                    )}
                  </Grid>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box sx={{ py: 8, bgcolor: "#f8fafc" }}>
        <Container>
          <Typography
            variant="h3"
            sx={{ textAlign: "center", mb: 2, fontWeight: "bold", fontSize: { xs: "1.6rem", sm: "2rem", md: "3rem" } }}
          >
            💎 Simple, Transparent Pricing
          </Typography>
          <Typography
            variant="h6"
            sx={{ textAlign: "center", mb: 4, opacity: 0.7 }}
          >
            Choose the perfect plan for your business
          </Typography>

          {/* Billing Period Toggle */}
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              mb: 6,
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'white',
                borderRadius: 10,
                p: 0.5,
                border: '2px solid #e2e8f0',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              }}
            >
              <Button
                onClick={() => setBillingPeriod('monthly')}
                sx={{
                  px: { xs: 2, md: 4 },
                  py: { xs: 1, md: 1.5 },
                  borderRadius: 8,
                  fontWeight: 'bold',
                  fontSize: { xs: '0.8rem', md: '1rem' },
                  background: billingPeriod === 'monthly' 
                    ? 'linear-gradient(135deg, #667eea, #764ba2)' 
                    : 'transparent',
                  color: billingPeriod === 'monthly' ? 'white' : '#64748b',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: billingPeriod === 'monthly' 
                      ? 'linear-gradient(135deg, #5a67d8, #6b46c1)' 
                      : 'rgba(102,126,234,0.1)',
                  },
                }}
              >
                Monthly
              </Button>
              <Button
                onClick={() => setBillingPeriod('yearly')}
                sx={{
                  px: { xs: 2, md: 4 },
                  py: { xs: 1, md: 1.5 },
                  borderRadius: 8,
                  fontWeight: 'bold',
                  fontSize: { xs: '0.8rem', md: '1rem' },
                  background: billingPeriod === 'yearly' 
                    ? 'linear-gradient(135deg, #667eea, #764ba2)' 
                    : 'transparent',
                  color: billingPeriod === 'yearly' ? 'white' : '#64748b',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  '&:hover': {
                    background: billingPeriod === 'yearly' 
                      ? 'linear-gradient(135deg, #5a67d8, #6b46c1)' 
                      : 'rgba(102,126,234,0.1)',
                  },
                }}
              >
                Yearly
                <Chip
                  label="Save 20%"
                  size="small"
                  sx={{
                    ml: 1,
                    display: { xs: 'none', sm: 'inline-flex' },
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                    height: '20px',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.05)' },
                    },
                  }}
                />
              </Button>
            </Box>
          </Box>

          <Grid
            container
            spacing={4}
            justifyContent="center"
            alignItems="stretch"
          >
            {pricingPlans.map((plan, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={index}
                sx={{ display: "flex" }}
              >
                <Card
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 4,
                    border: plan.popular
                      ? "3px solid #667eea"
                      : "1px solid #e2e8f0",
                    position: "relative",
                    background: plan.popular
                      ? "linear-gradient(135deg, #667eea, #764ba2)"
                      : "white",
                    color: plan.popular ? "white" : "inherit",
                    boxShadow: plan.popular
                      ? "0 20px 50px rgba(102,126,234,0.3)"
                      : "0 5px 20px rgba(0,0,0,0.1)",
                    transform: plan.popular ? { xs: "scale(1.01)", md: "scale(1.05)" } : "scale(1)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: plan.popular ? { xs: "scale(1.03)", md: "scale(1.08)" } : "scale(1.03)",
                      boxShadow: plan.popular
                        ? "0 25px 60px rgba(102,126,234,0.4)"
                        : "0 10px 30px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  {plan.popular && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -15,
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "linear-gradient(135deg, #ff4081, #ff6ec7)",
                        color: "white",
                        px: 3,
                        py: 1,
                        borderRadius: 4,
                        fontSize: "0.9rem",
                        fontWeight: "bold",
                        zIndex: 1,
                        boxShadow: "0 4px 15px rgba(255,64,129,0.3)",
                      }}
                    >
                      🎉 Most Popular
                    </Box>
                  )}

                  <CardContent
                    sx={{
                      p: 4,
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      justifyContent: "space-between",
                    }}
                  >
                    {/* Header Section */}
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        {plan.name}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          mb: 3,
                          opacity: 0.8,
                          minHeight: { xs: "auto", md: "50px" }, // Consistent description height
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {plan.description}
                      </Typography>

                      {/* Pricing */}
                      <Box sx={{ mb: 4 }}>
                        {billingPeriod === 'yearly' && plan.price !== 'Custom' && (
                          <Typography
                            variant="body2"
                            sx={{
                              textDecoration: 'line-through',
                              opacity: 0.5,
                              mb: 0.5,
                            }}
                          >
                            {getFormattedPrice(plan.price)}/mo
                          </Typography>
                        )}
                        <Typography
                          variant="h2"
                          sx={{
                            fontWeight: "bold",
                            fontSize: { xs: "2.5rem", md: "3.5rem" },
                          }}
                        >
                          {plan.price === 'Custom'
                            ? plan.price
                            : billingPeriod === 'yearly'
                            ? getYearlyPrice(plan.price)
                            : getFormattedPrice(plan.price)
                          }
                          <Typography
                            component="span"
                            variant="h6"
                            sx={{
                              opacity: 0.7,
                              fontSize: { xs: "1rem", md: "1.25rem" },
                            }}
                          >
                            {plan.price === 'Custom' ? '' : billingPeriod === 'yearly' ? '/mo' : plan.period}
                          </Typography>
                        </Typography>
                        {billingPeriod === 'yearly' && plan.price !== 'Custom' && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 1,
                              color: plan.popular ? 'rgba(255,255,255,0.8)' : '#10b981',
                              fontWeight: 'bold',
                            }}
                          >
                            {(() => {
                              const yp = getYearlyPrice(plan.price);
                              const sym = yp.replace(/[\d.,]+/, '');
                              const num = parseFloat(yp.replace(/[^0-9.]/g, ''));
                              return `Billed ${sym}${Math.round(num * 12)} annually`;
                            })()}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Features Section */}
                    <Box sx={{ mb: 4, flex: 1 }}>
                      {plan.features.map((feature, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            mb: 1.5,
                            textAlign: "left",
                          }}
                        >
                          <CheckCircle
                            sx={{
                              fontSize: 20,
                              mr: 1.5,
                              mt: 0.25,
                              color: plan.popular ? "#4ade80" : "#667eea",
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              lineHeight: 1.4,
                              fontSize: "0.95rem",
                            }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Button Section */}
                    <Box>
                      <Button
                        variant={plan.popular ? "outlined" : "contained"}
                        fullWidth
                        size="large"
                        onClick={() => handleGetStarted(plan.id)}
                        sx={{
                          borderRadius: 3,
                          py: 1.5,
                          fontWeight: "bold",
                          fontSize: "1.1rem",
                          textTransform: "none",
                          ...(plan.popular
                            ? {
                                borderColor: "white",
                                borderWidth: 2,
                                color: "white",
                                "&:hover": {
                                  background: "rgba(255,255,255,0.1)",
                                  borderColor: "white",
                                  transform: "translateY(-2px)",
                                },
                              }
                            : {
                                background:
                                  "linear-gradient(135deg, #667eea, #764ba2)",
                                color: "white",
                                border: "none",
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, #5a67d8, #6b46c1)",
                                  transform: "translateY(-2px)",
                                  boxShadow: "0 8px 25px rgba(102,126,234,0.4)",
                                },
                              }),
                        }}
                      >
                        {plan.price === "Free"
                          ? "Get Started Free"
                          : "Choose Plan"}
                      </Button>

                      {/* Optional: Add trial text for paid plans */}
                      {plan.price !== "Free" && (
                        <Typography
                          variant="caption"
                          sx={{
                            mt: 1,
                            opacity: 0.7,
                            display: "block",
                          }}
                        >
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Additional pricing info */}
          <Box sx={{ textAlign: "center", mt: 6 }}>
            <Typography variant="body1" sx={{ mb: 2, opacity: 0.7 }}>
              🔒 All plans include SSL encryption, 24/7 support, and 99.9%
              uptime guarantee
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              Need a custom solution?{" "}
              <Button
                variant="text"
                onClick={() => handleGetStarted()}
                sx={{ textDecoration: "underline" }}
              >
                Contact our sales team
              </Button>
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Customer Request Form Component */}
      <CustomerRequestForm
        open={contactFormOpen}
        onClose={handleContactFormClose}
        requestType={requestType}
        selectedPlan={selectedPlan} // Add this prop
      />

      {/* Footer Section */}
      <Footer onGetStarted={handleGetStarted} />
      
    </Box>
  );
};

export default LandingPage;
